import { useRef, useContext, ReactNode } from 'react'
import { motion } from 'framer-motion'
import { useInView } from '../hooks/useInView'
import { RouteTransitionContext } from './AnimatedRoutes'

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

type Props = {
  eyebrow: string
  title: string
  description?: string
  children?: ReactNode
  background?: 'cream' | 'white' | 'yellow'
}

const bgClass = {
  cream: 'bg-oak-cream',
  white: 'bg-white',
  yellow: 'bg-oak-yellow',
}

export default function PageHero({
  eyebrow,
  title,
  description,
  children,
  background = 'cream',
}: Props) {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.3 })
  const isTransitioning = useContext(RouteTransitionContext)

  return (
    <section
      ref={ref}
      className={`${bgClass[background]} px-6 pt-32 pb-20 md:pt-44 md:pb-28`}
    >
      <motion.div
        className="mx-auto max-w-5xl text-center"
        variants={stagger}
        initial="hidden"
        animate={!isTransitioning && isInView ? 'visible' : 'hidden'}
      >
        <motion.span
          variants={fadeUp}
          className="text-sm font-semibold uppercase tracking-widest text-gold-500"
        >
          {eyebrow}
        </motion.span>

        <motion.h1
          variants={fadeUp}
          className="mx-auto mt-4 max-w-[820px] font-serif text-[clamp(40px,7vw,96px)] leading-[0.95] tracking-[-2px] text-oak-black"
        >
          {title}
        </motion.h1>

        {description && (
          <motion.p
            variants={fadeUp}
            className="mx-auto mt-6 max-w-[560px] text-[clamp(16px,1.8vw,22px)] font-medium leading-[1.4] tracking-[-0.3px] text-oak-black/60 md:mt-8"
          >
            {description}
          </motion.p>
        )}

        {children && (
          <motion.div variants={fadeUp} className="mt-10 md:mt-12">
            {children}
          </motion.div>
        )}
      </motion.div>
    </section>
  )
}
